import React, { useState } from 'react';
import { Button } from './ui/button';
import { Check, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pricingPlans } from '../mock/data';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';

const Pricing = () => {
  const [activeTab] = useState('Mensuel');

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Title */}
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom duration-700">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Des abonnements <span className="text-[#52a675]">pensés pour vous</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            L'entretien auto réinventé : une formule accessible, modulable et sans frais cachés, pour une prise en charge claire, simple et sans surprise
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <Card
              key={plan.id}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl animate-in fade-in slide-in-from-bottom ${
                plan.popular
                  ? 'border-[#52a675] border-2 shadow-xl scale-105'
                  : 'border-gray-200 hover:border-[#52a675]'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-[#52a675] text-white px-4 py-1 text-sm font-semibold rounded-bl-lg flex items-center space-x-1">
                  <Star className="w-4 h-4 fill-current" />
                  <span>Populaire</span>
                </div>
              )}

              <CardHeader className="space-y-4 pb-8">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {plan.name}
                </CardTitle>
                <div className="flex items-baseline space-x-2">
                  <span className="text-sm text-gray-500">Dès</span>
                  <span className="text-5xl font-bold text-[#52a675]">{plan.price}</span>
                  <span className="text-gray-600">
                    {plan.currency}/{plan.period}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                {plan.features.map((feature, idx) => (
                  <div
                    key={idx}
                    className={`flex items-start space-x-3 ${
                      feature.indent ? 'ml-6' : ''
                    } ${
                      feature.isHeader ? 'mt-4 font-semibold text-gray-900' : 'text-gray-600'
                    }`}
                  >
                    {!feature.isHeader && (
                      <Check className="w-5 h-5 text-[#52a675] flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm leading-relaxed">{feature.text}</span>
                  </div>
                ))}
              </CardContent>

              <CardFooter className="pt-6">
                <Link to={`/calculateur/${plan.id.toUpperCase()}`} className="w-full">
                  <Button
                    className={`w-full py-6 group transition-all duration-300 ${
                      plan.popular
                        ? 'bg-[#52a675] hover:bg-[#3d8a5c] text-white shadow-lg'
                        : 'bg-gray-100 hover:bg-[#52a675] text-gray-900 hover:text-white'
                    }`}
                  >
                    {plan.ctaText}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Additional Offers */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-16">
          {/* Fleet */}
          <div className="bg-gradient-to-br from-[#52a675] to-[#3d8a5c] rounded-3xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 animate-in fade-in slide-in-from-left duration-700 delay-300">
            <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
              Abonnement flotte
            </span>
            <h3 className="text-3xl font-bold mt-4 mb-6">
              Famille ou entreprise, on s'adapte à vos besoins !
            </h3>
            <Link to="/contact">
              <Button
                variant="secondary"
                className="bg-white text-[#52a675] hover:bg-gray-100 shadow-lg group"
              >
                Obtenez un devis personnalisé
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {/* Partner */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 animate-in fade-in slide-in-from-right duration-700 delay-300">
            <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
              Devenir partenaire
            </span>
            <h3 className="text-3xl font-bold mt-4 mb-6">
              Vous êtes un vendeur automobile ou un garage ?
            </h3>
            <Link to="/contact">
              <Button
                variant="secondary"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg group"
              >
                Rejoignez notre réseau
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
